import { useMemo, useRef, useState } from "react";
import type { PanelType } from "./PanelSelector";
import type { SvgToolHandoffSlot } from "../../utils/svgToolHandoff";
import {
    navigateWithHandoff,
    openHandoffInNewTab,
    type SvgToolHandoffPayloadV1,
} from "../../utils/svgToolHandoff";
import {
    handoffOptionLabel,
    handoffOptionsForSlots,
    type SvgHandoffMenuOption,
} from "../../utils/svgToolHandoffTargets";
import "./SendSvgToToolMenu.scss";

export type SendSvgToToolMenuProps = {
    disabled?: boolean;
    getSvg: () => { name: string; content: string } | null;
    allowedSlots: SvgToolHandoffSlot[];
    /** Omit destinations (e.g. current tool). */
    excludeTargetPanels?: PanelType[];
    className?: string;
};

export function SendSvgToToolMenu({
    disabled = false,
    getSvg,
    allowedSlots,
    excludeTargetPanels,
    className,
}: SendSvgToToolMenuProps) {
    const shiftHeldRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const options = useMemo(
        () => handoffOptionsForSlots(allowedSlots, excludeTargetPanels),
        [allowedSlots, excludeTargetPanels],
    );

    const optionById = useMemo(() => {
        const m = new Map<string, SvgHandoffMenuOption>();
        for (const o of options) m.set(o.optionId, o);
        return m;
    }, [options]);

    const onSelect = (optionId: string, shiftKey: boolean) => {
        setError(null);
        const opt = optionById.get(optionId);
        if (!opt) return;
        const svg = getSvg();
        if (!svg?.content?.trim()) {
            setError("Nothing to send.");
            return;
        }
        const payload: SvgToolHandoffPayloadV1 = {
            v: 1,
            targetPanel: opt.targetPanel,
            slot: opt.slot,
            name: svg.name,
            content: svg.content,
        };
        try {
            if (shiftKey) navigateWithHandoff(payload);
            else openHandoffInNewTab(payload);
        } catch (e) {
            setError(
                e instanceof Error ? e.message : "Could not send SVG.",
            );
        }
    };

    if (options.length === 0) return null;

    const rootClass = ["send-svg-to-tool", className].filter(Boolean).join(" ");

    return (
        <div className={rootClass}>
            <label className="field-label send-svg-to-tool__label">
                <span className="send-svg-to-tool__label-text">Send to tool</span>
                <select
                    className="anchor-select send-svg-to-tool__select"
                    disabled={disabled}
                    defaultValue=""
                    title="Choose a tool. Opens in a new tab. Hold Shift while choosing to open in this tab."
                    onKeyDown={(e) => {
                        shiftHeldRef.current = e.shiftKey;
                    }}
                    onKeyUp={() => {
                        shiftHeldRef.current = false;
                    }}
                    onChange={(e) => {
                        const v = e.target.value;
                        const shiftKey = shiftHeldRef.current;
                        shiftHeldRef.current = false;
                        e.target.value = "";
                        if (!v) return;
                        onSelect(v, shiftKey);
                    }}
                >
                    <option value="" disabled>
                        {disabled ? "Not available" : "Choose tool..."}
                    </option>
                    {options.map((o) => (
                        <option key={o.optionId} value={o.optionId}>
                            {handoffOptionLabel(o)}
                        </option>
                    ))}
                </select>
            </label>
            {error && <p className="send-svg-to-tool__error">{error}</p>}
        </div>
    );
}
